import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, FileTextIcon } from '@radix-ui/react-icons';
import { useAuth } from '../context/AuthContext';
import {
  DIAS_SEMANA,
  agruparSemanas,
  formatDataBR,
  mensagemErro,
  nomeEscala,
  fimDeSemanaComVaga,
  turnosDoCiclo,
} from '../components/escala/escalaUtils';
import { obterCiclo } from '../services/escalaApi';
import './Escala.css';

/**
 * A escala no formato da planilha impressa: uma tabela por semana, com os
 * turnos empilhados e um nome confirmado por linha. Feriado ocupa a coluna
 * em amarelo, como na planilha antiga.
 */
const EscalaImpressaoPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [ciclo, setCiclo] = useState(null);
  const [erro, setErro] = useState(null);
  const [nomeCompleto, setNomeCompleto] = useState(false);

  useEffect(() => {
    obterCiclo(id)
      .then(setCiclo)
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar a escala.')));
  }, [id]);

  const turnos = useMemo(() => (ciclo ? turnosDoCiclo(ciclo.dias) : []), [ciclo]);
  const fds = useMemo(() => (ciclo ? fimDeSemanaComVaga(ciclo.dias) : { sabado: false, domingo: false }), [ciclo]);
  const { colunas, semanas } = useMemo(
    () => (ciclo ? agruparSemanas(ciclo.dias, fds) : { colunas: [], semanas: [] }),
    [ciclo, fds],
  );

  const voltar = user?.role === 'admin' ? `/admin/escala/${id}` : '/escala';

  if (erro) {
    return (
      <div className="esc-print-pagina">
        <div className="esc-print-barra">
          <Link to={voltar} className="btn-secondary esc-btn-link"><ArrowLeftIcon /> Voltar</Link>
        </div>
        <p style={{ padding: 24 }}>{erro}</p>
      </div>
    );
  }
  if (!ciclo) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="esc-print-pagina">
      <div className="esc-print-barra no-print">
        <Link to={voltar} className="btn-secondary esc-btn-link"><ArrowLeftIcon /> Voltar</Link>
        <label className="esc-check">
          <input type="checkbox" checked={nomeCompleto} onChange={(e) => setNomeCompleto(e.target.checked)} />
          Nome completo
        </label>
        <button className="btn-primary" onClick={() => window.print()}>
          <FileTextIcon /> Imprimir / salvar PDF
        </button>
      </div>

      <div className="esc-print">
        <header className="esc-print-cab">
          <img src={`${process.env.PUBLIC_URL}/emblema-cbmce.png`} alt="" className="esc-print-logo" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
          <div>
            <h1>CORPO DE BOMBEIROS MILITAR DO CEARÁ</h1>
            <h2>ESCALA DE MONITORES ITINERANTES — {ciclo.nome.toUpperCase()}</h2>
            <p>{formatDataBR(ciclo.dataInicio)} a {formatDataBR(ciclo.dataFim)}</p>
          </div>
        </header>

        {semanas.map((semana) => (
          <table className="esc-print-semana" key={semana.chave}>
            <thead>
              <tr>
                <th className="esc-print-turno-col">TURNO</th>
                {semana.celulas.map((dia, idx) => (
                  <th key={idx} className={dia?.feriado ? 'feriado' : ''}>
                    {dia ? (
                      <>
                        <span className="esc-print-dia-nome">{DIAS_SEMANA[dia.diaSemana].toUpperCase()}</span>
                        <span className="esc-print-dia-data">{formatDataBR(dia.data)}</span>
                      </>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {turnos.map((t) => {
                const linhas = Math.max(
                  1,
                  ...semana.celulas.map((dia) => {
                    if (!dia || dia.feriado) return 0;
                    const turno = dia.turnos.find((x) => x.turno === t.key);
                    return Math.max(turno?.vagas || 0, turno?.confirmados.length || 0);
                  }),
                );
                return (
                  <React.Fragment key={t.key}>
                    {Array.from({ length: linhas }).map((_, linha) => (
                      <tr key={`${t.key}-${linha}`} className={linha === 0 ? 'primeira' : ''}>
                        {linha === 0 && (
                          <td className="esc-print-turno-col" rowSpan={linhas}>{t.curto}</td>
                        )}
                        {semana.celulas.map((dia, idx) => {
                          if (!dia) return <td key={idx} className="vazio" />;
                          if (dia.feriado) {
                            return (
                              <td key={idx} className="feriado">
                                {linha === 0 ? 'FERIADO' : ''}
                                {linha === 1 && dia.descricao ? dia.descricao : ''}
                              </td>
                            );
                          }
                          const turno = dia.turnos.find((x) => x.turno === t.key);
                          if (!turno || turno.vagas === 0) return <td key={idx} className="sem-turno" />;
                          const c = turno.confirmados[linha];
                          return (
                            <td key={idx} className={c ? '' : 'livre'}>
                              {c ? nomeEscala(c, nomeCompleto) : linha < turno.vagas ? '' : ''}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ))}

        <footer className="esc-print-rodape">
          Gerado pelo sistema de Hora-Aula do CMCB em {new Date().toLocaleDateString('pt-BR')}
          {' · '}{colunas.length} dias por semana · {ciclo.resumo.confirmados} de {ciclo.resumo.vagasTotal} vagas preenchidas
        </footer>
      </div>
    </div>
  );
};

export default EscalaImpressaoPage;
